<template>
  <Teleport v-if="showUserContextMenu" to="body">
    <ul :style="contextMenuPosition" class="contextmenu" @click="onContextMenuUserClick">
      <template v-if="app.isTeacher()">
        <li v-for="(value, key) in permissions" :key="key">
          <label class="flex gap-1">
            <input
              v-model="contextUserModel.permissions[key]"
              type="checkbox"
              @click="setUsersPermission(key, ($event.target as HTMLInputElement).checked)"
            />
            <span v-html="value[1]" />
            {{ value[0] }}
          </label>
        </li>
      </template>
      <li v-if="app.isTeacher()" class="context-menu-divider" />
      <li v-for="item in contextMenuItemsUserActions" :key="item.join()">
        <span v-html="item[0]" />
        <span v-html="item[1]" />
      </li>
      <li v-if="app.isTeacher()" class="context-menu-divider" />
      <li v-for="item in contextMenuItemsUserInfo" :key="item.join()">
        <span v-html="item[0]" />
        <span>{{ item[1] }}</span>
      </li>
    </ul>
  </Teleport>

  <Teleport v-if="showRoomContextMenu" to="body">
    <ul :style="contextMenuPosition" class="contextmenu" @click="onContextMenuRoomClick">
      <template v-if="app.isTeacher()">
        <li v-for="item in contextMenuItemsRoom" :key="item" v-html="item" />
      </template>
    </ul>
  </Teleport>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick, type Ref, onBeforeUnmount } from 'vue';
import { getApp } from '../models/App';
import { Room } from '../models/Room';
import { User } from '../models/User';
import { permissions, type Permissions, MAIN_ROOM } from '../types';
import { confirm, alert, prompt } from '../utils';
import { whiteBoardAPI } from './whiteboardAPI';
import { MAX_ROOM_NAME_LENGTH } from '../config';

const app = getApp();

const contextMenuPosition = ref({});
const showUserContextMenu = ref(false);
const contextUser = ref('');
const showRoomContextMenu = ref(false);
const contextRoom = ref('');
const lastMouseEvent = ref<MouseEvent | null>(null);

const contextUserModel = computed(() => User.get(contextUser.value) as User);

const onContextMenuShowRoom = (ev: MouseEvent, room: string) => {
  // Close any existing context menus first
  showUserContextMenu.value = false;
  showRoomContextMenu.value = false;

  contextRoom.value = room;
  lastMouseEvent.value = ev; // Store the mouse event for repositioning

  setupContextMenuListeners(showRoomContextMenu);
  setContextMenuPosition(ev);
};

const onContextMenuShowUser = (ev: MouseEvent, userId: string) => {
  if (userId === app.connection.id) {
    // return  // Commenting this out temporarily to allow the displaying of jitsi IDs for teachers
  }

  // Close any existing context menus first
  showUserContextMenu.value = false;
  showRoomContextMenu.value = false;

  contextUser.value = userId;
  lastMouseEvent.value = ev; // Store the mouse event for repositioning

  const user = User.get(userId)!;

  if (!user.selected) {
    selectUser(ev, user);
  }

  setupContextMenuListeners(showUserContextMenu);
  setContextMenuPosition(ev);
};

const setContextMenuPosition = (ev: MouseEvent) => {
  // Set initial position
  contextMenuPosition.value = {
    top: ev.clientY + 'px',
    left: ev.clientX + 'px',
  };

  // Wait for the menu to render, then adjust position if needed
  nextTick(() => {
    const menu = document.querySelector('.contextmenu') as HTMLElement;
    if (!menu) return;

    const menuRect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let newTop = ev.clientY;
    let newLeft = ev.clientX;

    // Adjust horizontal position if menu would overflow right edge
    if (ev.clientX + menuRect.width > viewportWidth - 20) {
      newLeft = viewportWidth - menuRect.width - 20; // 20px margin from edge (increased from 10px)
    }

    // Adjust vertical position if menu would overflow bottom edge
    if (ev.clientY + menuRect.height > viewportHeight - 20) {
      newTop = viewportHeight - menuRect.height - 20; // 20px margin from edge (increased from 10px)
    }

    // Ensure menu doesn't go off the left edge
    if (newLeft < 20) {
      newLeft = 20; // 20px margin from left (increased from 10px)
    }

    // Ensure menu doesn't go off the top edge
    if (newTop < 20) {
      newTop = 20; // 20px margin from top (increased from 10px)
    }

    // Update position
    contextMenuPosition.value = {
      top: newTop + 'px',
      left: newLeft + 'px',
    };
  });
};

const setupContextMenuListeners = (showContextMenu: Ref<boolean>) => {
  showContextMenu.value = true;

  const onClick = (ev: MouseEvent) => {
    if ((ev.target as HTMLElement).closest('.contextmenu') !== null) {
      return;
    }

    document.removeEventListener('click', onClick, { capture: true });

    // Upon closing the context menu, Deselect the user(s) that were selected
    const user = User.get(contextUser.value) as User;
    const users = User.all().filter((u) => u.selected);
    if (user !== null) {
      users.forEach((user) => {
        user.selected = false;
      });
    }

    showContextMenu.value = false;
  };

  document.addEventListener('click', onClick, { capture: true });
};

const onContextMenuUserClick = (ev: MouseEvent) => {
  const item = (ev.target as HTMLElement).closest('li')?.children[1]!.textContent;

  const user = User.get(contextUser.value) as User;
  const users = User.all().filter((u) => u.selected);
  const userIds = users.map((u) => u.id).join(',');
  const conf = app.connection.currentRoom;

  if ((!user && !users.length) || !conf) return;
  switch (item) {
    case 'Lower Hand':
      users.forEach((user) => {
        app.raiseHand(user.id, false);

        // We don't want the user(s) to remain selected after this action is done.
        user.selected = false;
      });
      break;
    case 'Private Message User':
      app.currentPrivateChat = user.id;
      app.currentChat = user.id;

      // We don't want the user(s) to remain selected after this action is done.
      user.selected = false;

      break;
    case 'Kick User':
      // Make sure we don't accidentally kick a user
      confirm(
        `Are you sure you want to kick ${users.map((u) => u.name).join(', ')}?`,
        (yesOrNo) => {
          if (yesOrNo) {
            users.forEach((user) => {
              app.restrictedSend('[kick]' + user.id);
            });
          }
        },
        true,
      );
      break;
    case 'Send to Hallway': {
      // We don't want the user(s) to remain selected after this action is done.
      users.forEach((user) => {
        user.selected = false;
      });

      app.send(`[breakout]create:Hallway:1:yes:false:false:${userIds}`);
      break;
    }
    case 'Reset User Whiteboard': {
      users.forEach((user) => {
        app.restrictedSend(`[WhiteboardReset]${user.id}`);
      });
      break;
    }
    case 'Reset My Whiteboard': {
      whiteBoardAPI.reset(app.connection.currentRoom!.serverName);
      break;
    }
    case 'Confetti': {
      users.forEach((user) => {
        app.restrictedSend(`[confetti]${user.id}`);
      });
      break;
    }
  }

  nextTick(() => {
    showUserContextMenu.value = false;
  });
};

const contextMenuItemsUserActions = computed(() => {
  const items: string[][] = [];

  const user = User.get(contextUser.value);
  if (!user) return items;

  // Only let teachers have all abilities to manage users on right click
  if (app.isTeacher()) {
    items.push(['✋', 'Lower Hand']);
    items.push(['<i class="bx bxs-user-x" style="font-size: 18px; vertical-align: middle;"></i>', 'Kick User']);
    items.push(['<i class="bx bxs-door-open" style="font-size: 18px; vertical-align: middle;"></i>', 'Send to Hallway']);
    items.push(['<i class="bx bxs-pencil" style="font-size: 18px; vertical-align: middle;"></i>', 'Reset User Whiteboard']);
    items.push(['<i class="bx bxs-party" style="font-size: 18px; vertical-align: middle;"></i>', 'Confetti']);
  }

  if (user.id !== getApp().connection.id) {
    items.push([
      '<i class="bx bxs-message-alt-check" style="font-size: 18px; vertical-align: middle;"></i>',
      'Private Message User',
    ]);
  } else {
    if (!app.isTeacher()) {
      items.push([
        '<i class="bx bxs-pencil" style="font-size: 18px; vertical-align: middle;"></i>',
        '<span title="Useful for if you end up on a different board, can\'t see something on the board, etc.">Reset My Whiteboard</span>',
      ]);
    }
  }

  return items;
});

const contextMenuItemsUserInfo = computed(() => {
  const items: string[][] = [];

  const user = User.get(contextUser.value);
  if (!user) return items;

  // Let teachers...
  if (app.isTeacher()) {
    // See the amount of time that a user has been marked as away and absent for during a session.
    const currentAwayTime = user.awayTime + (user.awayTimeStamp ? nowRef.value - user.awayTimeStamp : 0);
    const currentAbsentTime = user.absentTime + (user.absentTimeStamp ? nowRef.value - user.absentTimeStamp : 0);
    const awayDate = new Date(currentAwayTime);
    const absentDate = new Date(currentAbsentTime);

    items.push([
      '⏳',
      `Time Away: ${awayDate.getMinutes().toString().padStart(1, '0')}:${awayDate.getSeconds().toString().padStart(2, '0')}`,
    ]);
    items.push([
      '<i class="bx bxs-timer" style="font-size: 18px; vertical-align: middle;"></i>',
      `Time Absent: ${absentDate.getMinutes().toString().padStart(1, '0')}:${absentDate
        .getSeconds()
        .toString()
        .padStart(2, '0')}`,
    ]);
    items.push([
      '<i class="bx bxs-user-x" style="font-size: 18px; vertical-align: middle;"></i>',
      `Times Kicked: ${user.kickedCount}`,
      '',
    ]);

    items.push(['<i class="bx bxs-user" style="font-size: 18px; vertical-align: middle;"></i>', `User ID: ${user.id}`]);
    items.push(['<i class="bx bxs-user" style="font-size: 18px; vertical-align: middle;"></i>', `Full Name: ${user.fullName}`]);
    items.push([
      '<i class="bx bxs-cog" style="font-size: 18px; vertical-align: middle;"></i>',
      `Browser: ${user.browser.browserName} ${user.browser.browserVersion}`,
    ]); // See the user's id on right click for debugging purposes
  }

  return items;
});

const nowRef = ref(Date.now());
let nowRefTimeout: ReturnType<typeof setTimeout> | undefined;
const updateNowRef = () => {
  nowRef.value = Date.now();
  nowRefTimeout = setTimeout(updateNowRef, 1000);
};

updateNowRef();

// Debounced resize handler
let resizeTimeout: ReturnType<typeof setTimeout> | undefined;
const handleWindowResize = () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }

  resizeTimeout = setTimeout(() => {
    // Only reposition if a context menu is open and we have the original mouse event
    if ((showUserContextMenu.value || showRoomContextMenu.value) && lastMouseEvent.value) {
      setContextMenuPosition(lastMouseEvent.value);
    }
  }, 150); // 150ms debounce
};

// Add resize listener when component mounts
window.addEventListener('resize', handleWindowResize);

const onContextMenuRoomClick = async (ev: MouseEvent) => {
  const item = (ev.target as HTMLElement).closest('li')?.textContent?.trim();
  const room = Room.get(contextRoom.value);

  // Make sure the context menu gets closed.
  nextTick(() => {
    showRoomContextMenu.value = false;
  });

  // Ensure we have a room object (or is this a class???)
  if (!room || !item) return;

  switch (item) {
    case 'Rename Room': {
      // Open the prompt to specify the new room name
      const newRoomName = await prompt(`Rename ${room.displayName} to:`);

      // Make sure something was entered for the new room name.
      if (!newRoomName) return;

      // Do not allow a breakout room to be called "Main Room".
      if (newRoomName === MAIN_ROOM) return;

      // Make sure the new room name doesn't match one of the existing ones.
      const roomDoesExist = app.connection.rooms.some((r) => {
        return newRoomName === r.displayName;
      });
      if (roomDoesExist) {
        alert('Whoops!  A room with that name already exists.  Please try again.');
        return;
      }
      if (newRoomName.length > MAX_ROOM_NAME_LENGTH) {
        alert(`Uh oh! Keep the room name below ${MAX_ROOM_NAME_LENGTH} characters please.`);
        return;
      }

      // Having a / in the room name causes problems with the whiteboard room/naming convention.
      if (newRoomName.includes('/')) {
        alert('Breakout room names cannot contain the "/" character.');
        return;
      }

      // Rename the room
      app.renameRoom(room.name, newRoomName);
      break;
    }
    case 'Select Random Student': {
      // Send request to select random student in this room
      app.restrictedSend(`[selectRandomStudent]${room.name}`);
      break;
    }
  }
};

const contextMenuItemsRoom = computed(() => {
  const items: string[] = [];

  const room = Room.get(contextRoom.value);

  if (!room) return items;

  // Only teachers can rename rooms, but they can't rename the Main Room.
  if (app.isTeacher() && room.name !== MAIN_ROOM) {
    items.push('Rename Room');
  }

  // Add random student selector for teachers
  if (app.isTeacher()) {
    const studentsInRoom = room.sortedUsers.filter((u) => u.role === 'student' && u.visible);
    if (studentsInRoom.length > 0) {
      items.push('Select Random Student');
    }
  }

  return items;
});

const selectUser = (ev: MouseEvent, user: User) => {
  // Prevent students from selecting people since they can't perform any actions with selected users anyway.
  if (!app.isTeacher()) return;

  if (!ev.ctrlKey) {
    User.all().forEach((u) => {
      u.selected = false;
    });
  }

  user.selected = ev.ctrlKey ? !user.selected : true;
};

// For setting permissions for all selected users (not teachers though)
const setUsersPermission = (key: keyof Permissions, checked: boolean) => {
  // Get all selected users
  const users = User.all().filter((u) => u.selected);

  // Set the permission for each selected user
  users.forEach((user) => {
    app.setUserPermission(user.id, key, checked);
    // app.setUserPermission(contextUser, key, $event.target.checked)
  });

  nextTick(() => {
    showUserContextMenu.value = false;
  });
};

onBeforeUnmount(() => {
  clearTimeout(nowRefTimeout);
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  window.removeEventListener('resize', handleWindowResize);
});

defineExpose({
  openRoomContextMenu: onContextMenuShowRoom,
  openUserContextMenu: onContextMenuShowUser,
});
</script>

<style scoped>
.contextmenu {
  position: absolute;
  background: white;
  border: none;
  border-radius: 6px;
  padding: 4px 0;
  margin: 0;
  list-style: none;
  z-index: 10; /* Ensuring menus open on top of the whiteboard / displayarea <div> */
  max-height: calc(100vh - 40px); /* Maximum height with 40px total margin (increased from 20px) */
  max-width: calc(100vw - 40px); /* Maximum width with 40px total margin (increased from 20px) */
  overflow-y: auto; /* Enable vertical scrolling when needed */
  overflow-x: hidden; /* Prevent horizontal scrolling */
  overscroll-behavior: none; /* Prevent scroll propagation to elements below */
  min-width: 180px; /* Minimum width for readability */
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.08); /* Modern layered shadow */
  backdrop-filter: blur(8px); /* Subtle blur effect */
  border: 1px solid rgba(0, 0, 0, 0.06); /* Very subtle border */
}

.contextmenu:empty {
  display: none; /* Hide the context menu when it has no content */
}

.contextmenu > li {
  padding: 6px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap; /* Prevent text wrapping */
  flex-shrink: 0; /* Prevent items from shrinking */
  color: #374151; /* Modern gray text */
  font-size: 13px;
  font-weight: 500;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  margin: 0px 1px;
  border-radius: 3px;
}

.contextmenu > li:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.contextmenu > li:active {
  background: #e5e7eb;
}

.contextmenu > li > span {
  display: flex;
  align-items: center;
}

.contextmenu > li > span:first-child {
  min-width: 18px;
  display: flex;
  justify-content: center;
  color: #6b7280; /* Icon color */
}

.contextmenu > li:hover > span:first-child {
  color: #374151; /* Darker icon on hover */
}

.context-menu-divider {
  border-top: 1px solid #e5e7eb;
  margin: 2px 0;
  padding: 0px !important;
}

/* Custom scrollbar styling for webkit browsers */
.contextmenu::-webkit-scrollbar {
  width: 6px;
}

.contextmenu::-webkit-scrollbar-track {
  background: transparent;
}

.contextmenu::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.contextmenu::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Checkbox styling for permissions */
.contextmenu input[type='checkbox'] {
  width: 14px;
  height: 14px;
  border-radius: 2px;
  border: 1.5px solid #d1d5db;
  background: white;
  cursor: pointer;
  position: relative;
  margin: 0;
}

.contextmenu input[type='checkbox']:checked {
  background: #3b82f6;
  border-color: #3b82f6;
}

.contextmenu label {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
  width: 100%;
}
</style>
